import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ReceiptLong as ReceiptIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Company,
  COMPANY_TIPO_LABELS,
  COMPANY_FONTE_LABELS,
} from "../types/company";
import { CompanyService } from "../services/companyService";

export const CompanyManager: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  // Navegação para Dashboard
  const navigateToInvoices = (nif: string) => {
    // window.dispatchEvent custom event para Dashboard
    window.dispatchEvent(
      new CustomEvent("navigateToInvoices", { detail: { nif } })
    );
  };
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Company>>({});
  const [lookingUp, setLookingUp] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const [filterPending, setFilterPending] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPending]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const params = filterPending
        ? { necessita_preenchimento: true, limit: 100 }
        : { limit: 100 };

      const response = await CompanyService.getAll(params);
      setCompanies(response.data);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
      showSnackbar("Erro ao carregar empresas", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company: Company) => {
    setEditingId(company.id);
    setEditForm({
      nome: company.nome || "",
      nome_curto: company.nome_curto || "",
      tipo: company.tipo || "empresa",
      email: company.email || "",
      telefone: company.telefone || "",
      morada: company.morada || "",
      codigo_postal: company.codigo_postal || "",
      cidade: company.cidade || "",
      website: company.website || "",
      notas: company.notas || "",
    });
  };

  const handleSave = async (nif: string) => {
    try {
      await CompanyService.update(nif, {
        ...editForm,
        necessita_preenchimento: false,
        verificado: true,
      });

      showSnackbar("Empresa atualizada com sucesso!", "success");
      setEditingId(null);
      loadCompanies();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      showSnackbar("Erro ao salvar empresa", "error");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleLookup = async (nif: string) => {
    try {
      setLookingUp(nif);
      const response = await CompanyService.lookup(nif);

      if (response.data.necessita_preenchimento) {
        showSnackbar(
          "Empresa não encontrada automaticamente. Preencha manualmente.",
          "warning"
        );
      } else {
        showSnackbar("Dados obtidos com sucesso!", "success");
        loadCompanies();
      }
    } catch (error) {
      console.error("Erro no lookup:", error);
      showSnackbar("Erro ao buscar dados", "error");
    } finally {
      setLookingUp(null);
    }
  };

  const handleBatchLookup = async () => {
    try {
      const nifs = companies
        .filter((c) => c.necessita_preenchimento)
        .map((c) => c.nif);

      if (nifs.length === 0) {
        showSnackbar("Nenhuma empresa pendente", "info");
        return;
      }

      showSnackbar(`Buscando dados de ${nifs.length} empresas...`, "info");
      await CompanyService.batchLookup(nifs);

      setTimeout(() => {
        loadCompanies();
        showSnackbar(
          "Lookup em lote iniciado! Aguarde alguns minutos.",
          "success"
        );
      }, 2000);
    } catch (error) {
      console.error("Erro no batch lookup:", error);
      showSnackbar("Erro ao iniciar lookup em lote", "error");
    }
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;

    setDeleting(true);
    try {
      // Verifica se há referências
      const refCheck = await CompanyService.checkReferences(
        companyToDelete.nif
      );

      if (refCheck.data.isReferenced) {
        showSnackbar(
          `Não é possível deletar: empresa tem ${refCheck.data.count} fatura(s) associada(s)`,
          "error"
        );
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
        return;
      }

      // Se não tiver referências, deleta permanentemente
      await CompanyService.deletePermanent(companyToDelete.nif);
      showSnackbar("Empresa deletada com sucesso!", "success");
      loadCompanies();
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    } catch (error: any) {
      console.error("Erro ao deletar empresa:", error);
      const message = error.response?.data?.error || "Erro ao deletar empresa";
      showSnackbar(message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.nif.includes(searchTerm) ||
      company.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.nome_curto?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header com filtros e ações */}
      <Stack
        direction="row"
        spacing={2}
        mb={3}
        alignItems="center"
        flexWrap="wrap"
      >
        <TextField
          placeholder="Buscar por NIF ou nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: "250px" }}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtro</InputLabel>
          <Select
            value={filterPending ? "pending" : "all"}
            label="Filtro"
            onChange={(e) => setFilterPending(e.target.value === "pending")}
          >
            <MenuItem value="pending">Apenas pendentes</MenuItem>
            <MenuItem value="all">Todas</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleBatchLookup}
          disabled={
            companies.filter((c) => c.necessita_preenchimento).length === 0
          }
        >
          Buscar Todos (
          {companies.filter((c) => c.necessita_preenchimento).length})
        </Button>

        <IconButton onClick={loadCompanies} color="primary">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Estatísticas */}
      <Stack direction="row" spacing={2} mb={3}>
        <Alert severity="info" sx={{ flex: 1 }}>
          <strong>{companies.length}</strong> empresas no total
        </Alert>
        <Alert severity="warning" sx={{ flex: 1 }}>
          <strong>
            {companies.filter((c) => c.necessita_preenchimento).length}
          </strong>{" "}
          precisam de dados
        </Alert>
        <Alert severity="success" sx={{ flex: 1 }}>
          <strong>
            {companies.filter((c) => !c.necessita_preenchimento).length}
          </strong>{" "}
          completas
        </Alert>
      </Stack>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="10%">
                <strong>NIF</strong>
              </TableCell>
              <TableCell width="20%">
                <strong>Nome</strong>
              </TableCell>
              <TableCell width="15%">
                <strong>Nome Curto</strong>
              </TableCell>
              <TableCell width="10%">
                <strong>Tipo</strong>
              </TableCell>
              <TableCell width="15%">
                <strong>Email</strong>
              </TableCell>
              <TableCell width="10%">
                <strong>Fonte</strong>
              </TableCell>
              <TableCell width="10%">
                <strong>Status</strong>
              </TableCell>
              <TableCell width="10%" align="right">
                <strong>Ações</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhuma empresa encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map((company) => {
                const isEditing = editingId === company.id;

                return (
                  <TableRow
                    key={company.id}
                    sx={{
                      bgcolor: company.necessita_preenchimento
                        ? "warning.50"
                        : "inherit",
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {company.nif}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.nome || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, nome: e.target.value })
                          }
                          placeholder="Nome completo"
                        />
                      ) : (
                        <Typography variant="body2">
                          {company.nome || "-"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.nome_curto || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              nome_curto: e.target.value,
                            })
                          }
                          placeholder="Nome curto"
                        />
                      ) : (
                        <Typography variant="body2">
                          {company.nome_curto || "-"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <Select
                          fullWidth
                          size="small"
                          value={editForm.tipo || "empresa"}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              tipo: e.target.value as any,
                            })
                          }
                        >
                          <MenuItem value="empresa">Empresa</MenuItem>
                          <MenuItem value="individual">Individual</MenuItem>
                          <MenuItem value="gov">Governo</MenuItem>
                          <MenuItem value="outro">Outro</MenuItem>
                        </Select>
                      ) : (
                        <Typography variant="body2">
                          {company.tipo
                            ? COMPANY_TIPO_LABELS[company.tipo]
                            : "-"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.email || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, email: e.target.value })
                          }
                          placeholder="email@example.com"
                        />
                      ) : (
                        <Typography variant="body2" fontSize="0.8rem">
                          {company.email || "-"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          company.fonte
                            ? COMPANY_FONTE_LABELS[company.fonte]
                            : "-"
                        }
                        size="small"
                        color={
                          company.fonte === "VIES" ||
                          company.fonte === "OpenCorporates" ||
                          company.fonte === "NIF.PT"
                            ? "success"
                            : "default"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      {company.necessita_preenchimento ? (
                        <Chip
                          icon={<WarningIcon />}
                          label="Pendente"
                          size="small"
                          color="warning"
                        />
                      ) : (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Completa"
                          size="small"
                          color="success"
                        />
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        {isEditing ? (
                          <>
                            <Tooltip title="Salvar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSave(company.nif)}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={handleCancel}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Buscar automaticamente">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleLookup(company.nif)}
                                disabled={lookingUp === company.nif}
                              >
                                {lookingUp === company.nif ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SearchIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar manualmente">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(company)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ver faturas deste NIF">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => navigateToInvoices(company.nif)}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deletar empresa">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(company)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmação de delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja deletar permanentemente a empresa{" "}
            <strong>{companyToDelete?.nome || companyToDelete?.nif}</strong>?
            <br />
            <br />
            Esta ação só será possível se a empresa não tiver faturas
            associadas.
            <br />
            <strong>Esta operação não pode ser desfeita!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? "Deletando..." : "Deletar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
